import type {
  BookedFlightItineraryWithDepartureTime,
  AirBookingFlightItinerarySlice,
} from "@b2bportal/air-booking-api";
import type { Slice as ShoppedSlice } from "@b2bportal/air-shopping-api";
import type { AirportMap } from "@b2bportal/core-flights";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type SliceDirection,
} from "@b2bportal/core-types";
import {
  FlightItineraryDetailSliceSummary,
  FlightItinerarySliceDetails,
  useFlightExchangeSliceDetailsCard,
} from "@tenant/flights";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  Icon,
  Pill,
  PillStrength,
  PillTone,
} from "@tenant/ui";
import clsx from "clsx";

import defaultStyles from "./FlightExchangeSliceDetailsCard.module.scss";

export interface IFlightExchangeSliceDetailsCardProps {
  airportMap: AirportMap;
  className?: string;
  isUpdated: boolean;
  updateBadgeCopyOverride?: String;
  itinerary: BookedFlightItineraryWithDepartureTime;
  slice: AirBookingFlightItinerarySlice | ShoppedSlice;
  sliceDirection: SliceDirection;
}

export const FlightExchangeSliceDetailsCard = (
  props: IFlightExchangeSliceDetailsCardProps
) => {
  const COMPONENT_KEY = CoreFlightsComponent.FlightExchangeSliceDetailsCard;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { context, handlers, text } = useFlightExchangeSliceDetailsCard(props);

  return (
    <div className={clsx(block, props.className)}>
      <span className={cn("slice-label")}>{text.sliceLabel}</span>
      <div className={cn("slice-summary-card", { updated: context.isUpdated })}>
        <div className={cn("slice-summary-card-body")}>
          <div className={cn("slice-summary-badge")}>
            {context.isUpdated && (
              <Pill tone={PillTone.info} strength={PillStrength.subtle}>
                {text.updatedPillMessage}
              </Pill>
            )}
          </div>
          <FlightItineraryDetailSliceSummary
            itinerary={props.itinerary}
            slice={context.slice}
          />
        </div>
        <div className={cn("slice-summary-card-actions")}>
          <Dialog>
            <DialogTrigger>
              <div className={cn("slice-summary-cta")}>
                {text.viewSliceDetailsCta}
                <Icon
                  useCurrentColor
                  className={cn("slice-summary-cta-icon")}
                  iconName={IconNameEnum.chevronDown}
                />
              </div>
            </DialogTrigger>
            <DialogContent title={text.sliceDetailsTitle}>
              <FlightItinerarySliceDetails
                destination={context.sliceDestination}
                direction={context.sliceDirection}
                formattedDepartureDate={context.sliceDepartureDate}
                itinerary={props.itinerary.bookedItinerary}
                slice={context.slice}
              />
            </DialogContent>
          </Dialog>
          {context.isUpdated && (
            <div
              className={cn("slice-summary-cta")}
              onClick={handlers.restartExchange}
            >
              {text.editFlightCta}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
